(ns iris.anchor-scroll
  (:require
   [clojure.string :as string]))

(def default-options
  {:attempts 30
   :interval-ms 50
   :stable-frames 3
   :min-found-attempts 6
   :offset-margin 6
   :scroll-tolerance 2
   :scroll-intent-window-ms 500})

(defonce last-user-scroll-intent-ms (atom 0))
(defonce user-scroll-intent-listeners-attached? (atom false))

(def scroll-keys
  #{"ArrowDown" "ArrowLeft" "ArrowRight" "ArrowUp"
    "End" "Home" "PageDown" "PageUp" " "})

(defn mark-user-scroll-intent! []
  (reset! last-user-scroll-intent-ms (.now js/Date)))

(defn scroll-key-event? [e]
  (contains? scroll-keys (.-key e)))

(defn attach-user-scroll-intent-listeners! []
  (when (compare-and-set! user-scroll-intent-listeners-attached? false true)
    (.addEventListener js/window "wheel" mark-user-scroll-intent! #js {:passive true})
    (.addEventListener js/window "touchmove" mark-user-scroll-intent! #js {:passive true})
    (.addEventListener js/window
                       "keydown"
                       #(when (scroll-key-event? %) (mark-user-scroll-intent!))
                       #js {:passive true})))

(defn current-anchor []
  (let [hash (.. js/window -location -hash)]
    (when (seq hash)
      (subs hash 1))))

(defn href-anchor [href]
  (second (string/split href #"#" 2)))

(defn href-anchor-targets [href]
  (let [anchor (href-anchor href)
        thread-id (second (re-find #"/t/([^#?]+)" href))]
    (remove string/blank?
            [anchor
             (when (and anchor thread-id) (str anchor "+" thread-id))
             (when (and anchor thread-id) (str "@" anchor "+" thread-id))])))

(defn element-by-ruid [target]
  (let [nodes (.querySelectorAll js/document "[ruid]")]
    (loop [idx 0]
      (when (< idx (.-length nodes))
        (let [el (.item nodes idx)]
          (if (= target (.getAttribute el "ruid"))
            el
            (recur (inc idx))))))))

(defn element-by-target [target]
  (or (.getElementById js/document target)
      (element-by-ruid target)))

(defn sticky-offset [offset-margin]
  (if-let [navbar (.getElementById js/document "navbar")]
    (let [rect (.getBoundingClientRect navbar)
          bottom (.-bottom rect)]
      (if (pos? bottom)
        (+ bottom offset-margin)
        offset-margin))
    offset-margin))

(defn element-document-rect [el]
  (let [rect (.getBoundingClientRect el)]
    {:x (+ (.-pageXOffset js/window) (.-left rect))
     :y (+ (.-pageYOffset js/window) (.-top rect))
     :width (.-width rect)
     :height (.-height rect)}))

(defn same-rect? [a b]
  (and a b
       (= (:x a) (:x b))
       (= (:y a) (:y b))
       (= (:width a) (:width b))
       (= (:height a) (:height b))))

(defn scroll-element-to-top! [el offset-margin]
  (let [{:keys [y]} (element-document-rect el)
        top (max 0 (- y (sticky-offset offset-margin)))]
    (.scrollTo js/window #js {:top top :behavior "auto"})
    top))

(defn recent-user-scroll-intent? [started-at intent-window-ms]
  (let [intent-at @last-user-scroll-intent-ms]
    (and (>= intent-at started-at)
         (<= (- (.now js/Date) intent-at) intent-window-ms))))

(defn scroll-away-from-target? [expected-scroll-top tolerance]
  (and (some? expected-scroll-top)
       (> (js/Math.abs (- (.-pageYOffset js/window) expected-scroll-top))
          tolerance)))

(defn scroll-to-targets!
  ([targets] (scroll-to-targets! targets nil))
  ([targets options]
   (let [{:keys [attempts interval-ms stable-frames min-found-attempts offset-margin
                 scroll-tolerance scroll-intent-window-ms]}
         (merge default-options options)
         started-at (.now js/Date)
         targets (distinct (remove string/blank? targets))]
     (when (seq targets)
       (attach-user-scroll-intent-listeners!)
       (letfn [(schedule! [attempts-left found-attempts stable-count previous-rect expected-scroll-top]
                 (js/setTimeout
                  #(attempt! attempts-left
                             found-attempts
                             stable-count
                             previous-rect
                             expected-scroll-top)
                  interval-ms))
               (attempt! [attempts-left found-attempts stable-count previous-rect expected-scroll-top]
                 (.requestAnimationFrame
                  js/window
                  (fn []
                    (when-not (and (scroll-away-from-target? expected-scroll-top scroll-tolerance)
                                   (recent-user-scroll-intent? started-at scroll-intent-window-ms))
                      (if-let [el (some element-by-target targets)]
                        (let [current-rect (element-document-rect el)
                              stable-count (if (same-rect? previous-rect current-rect)
                                             (inc stable-count)
                                             0)
                              found-attempts (inc found-attempts)
                              expected-scroll-top (scroll-element-to-top! el offset-margin)]
                          (when (and (pos? attempts-left)
                                     (or (< found-attempts min-found-attempts)
                                         (< stable-count stable-frames)))
                            (schedule! (dec attempts-left)
                                       found-attempts
                                       stable-count
                                       current-rect
                                       expected-scroll-top)))
                        (when (pos? attempts-left)
                          (schedule! (dec attempts-left)
                                     found-attempts
                                     0
                                     nil
                                     expected-scroll-top)))))))]
         (attempt! attempts 0 0 nil nil))))))

(defn scroll-to-anchor! [anchor]
  (scroll-to-targets! [anchor]))

(defn scroll-to-href! [href]
  (scroll-to-targets! (href-anchor-targets href)))
